import { useState } from 'react';
import type { ColumnSchema, TransformStep } from '../../api/datasets';
import { Button, Input } from '../ui';
import { STEP_CATALOG, stepDefinition, toApiStep, type StepField } from './stepCatalog';
import './StepForm.css';

export interface StepFormProps {
  columns: ColumnSchema[];
  onAdd: (step: TransformStep) => void;
  onCancel: () => void;
}

/** The form for one new step, generated from the step catalogue. */
export function StepForm({ columns, onAdd, onCancel }: StepFormProps) {
  const [operation, setOperation] = useState(STEP_CATALOG[0]!.op);
  const [values, setValues] = useState<Record<string, unknown>>({});

  const definition = stepDefinition(operation)!;

  function set(key: string, value: unknown) {
    setValues((current) => ({ ...current, [key]: value }));
  }

  function renderField(field: StepField) {
    if (field.kind === 'column') {
      return (
        <label key={field.key} className="sc-stepform__field">
          <span>{field.label}</span>
          <select
            value={String(values[field.key] ?? '')}
            onChange={(event) => set(field.key, event.target.value)}
          >
            <option value="">Choose a column…</option>
            {columns.map((column) => (
              <option key={column.name} value={column.name}>
                {column.source_name}
              </option>
            ))}
          </select>
        </label>
      );
    }

    if (field.kind === 'columns') {
      const selected = (values[field.key] as string[]) ?? [];
      return (
        <fieldset key={field.key} className="sc-stepform__field">
          <legend>
            {field.label}
            {field.optional && <span className="sc-stepform__optional"> (optional)</span>}
          </legend>
          <div className="sc-stepform__checks">
            {columns.map((column) => (
              <label key={column.name} className="sc-stepform__check">
                <input
                  type="checkbox"
                  checked={selected.includes(column.name)}
                  onChange={(event) =>
                    set(
                      field.key,
                      event.target.checked
                        ? [...selected, column.name]
                        : selected.filter((name) => name !== column.name),
                    )
                  }
                />
                {column.source_name}
              </label>
            ))}
          </div>
        </fieldset>
      );
    }

    if (field.kind === 'select') {
      return (
        <label key={field.key} className="sc-stepform__field">
          <span>{field.label}</span>
          <select
            value={String(values[field.key] ?? field.options?.[0]?.value ?? '')}
            onChange={(event) => set(field.key, event.target.value)}
          >
            {field.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      );
    }

    return (
      <Input
        key={field.key}
        label={field.label + (field.optional ? ' (optional)' : '')}
        type={field.kind === 'number' ? 'number' : 'text'}
        hint={field.hint}
        value={String(values[field.key] ?? '')}
        onChange={(event) =>
          set(field.key, field.kind === 'number' ? Number(event.target.value) : event.target.value)
        }
      />
    );
  }

  return (
    <div className="sc-stepform">
      <label className="sc-stepform__field">
        <span>Step</span>
        <select
          value={operation}
          onChange={(event) => {
            setOperation(event.target.value as typeof operation);
            setValues({});
          }}
        >
          {STEP_CATALOG.map((entry) => (
            <option key={entry.op} value={entry.op}>
              {entry.label}
            </option>
          ))}
        </select>
      </label>
      <p className="sc-stepform__description">{definition.description}</p>

      {definition.fields.map(renderField)}

      <div className="sc-stepform__actions">
        <Button onClick={() => onAdd(toApiStep(operation, values))}>Add step</Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
